<template>
  <div class="contract-container">
    <div class="page-header">
      <h2>合同管理</h2>
      <p>管理公司合同信息</p>
    </div>
    
    <div class="content-area">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>合同列表</span>
            <div class="header-actions">
              <el-button type="primary" size="small" @click="handleAdd">新建合同</el-button>
              <el-button type="success" size="small" @click="handleRefresh">刷新数据</el-button>
            </div>
          </div>
        </template>
        
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-form :inline="true" :model="searchForm" class="demo-form-inline">
            <el-form-item label="合同名称">
              <el-input v-model="searchForm.name" placeholder="请输入合同名称" clearable />
            </el-form-item>
            <el-form-item label="关联项目">
              <el-select v-model="searchForm.project" placeholder="请选择关联项目" style="width: 200px;" clearable>
                <el-option
                  v-for="proj in projectList"
                  :key="proj.id"
                  :label="proj.name"
                  :value="proj.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch">查询</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 合同列表 -->
        <el-table
          :data="contractList"
          v-loading="loading"
          style="width: 100%"
          @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="name" label="合同名称" width="200" show-overflow-tooltip />
          <el-table-column prop="project" label="关联项目" width="180">
            <template #default="scope">
              <div class="project-list">
                <el-tag
                  v-for="projectId in getProjectIds(scope.row.project)"
                  :key="projectId"
                  size="small"
                  class="project-tag">
                  {{ getProjectName(projectId) }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="file" label="关联文件" width="180">
            <template #default="scope">
              <div class="file-list">
                <el-tag
                  v-for="(file, index) in getFileList(scope.row.file)"
                  :key="index"
                  size="small"
                  type="info"
                  class="file-tag"
                  @click="handleFilePreview(file)">
                  {{ getFileName(file) }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="createtime" label="创建时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.createtime) }}
            </template>
          </el-table-column>
          <el-table-column prop="updatetime" label="更新时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.updatetime) }}
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" show-overflow-tooltip />
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            v-model:current-page="pagination.currentPage"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange" />
        </div>
      </el-card>
    </div>

    <!-- 添加/编辑合同对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="700px"
      @close="handleDialogClose">
      <el-form
        ref="contractFormRef"
        :model="contractForm"
        :rules="contractRules"
        label-width="100px">
        <el-form-item label="合同名称" prop="name">
          <el-input v-model="contractForm.name" placeholder="请输入合同名称" />
        </el-form-item>
        <el-form-item label="关联项目">
          <el-select
            v-model="contractForm.project"
            multiple
            placeholder="请选择关联项目（可选）"
            style="width: 100%">
            <el-option
              v-for="proj in projectList"
              :key="proj.id"
              :label="proj.name"
              :value="proj.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="关联文件">
          <el-upload
            ref="uploadRef"
            class="upload-demo"
            :action="uploadUrl"
            :headers="headers"
            :data="uploadData"
            :file-list="fileList"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-remove="handleFileRemove"
            :before-upload="beforeUpload"
            multiple
            :limit="10"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png">
            <el-button type="primary">选择文件</el-button>
            <template #tip>
              <div class="el-upload__tip">
                支持上传 PDF、Word、Excel、图片等格式文件，单个文件不超过 10MB
              </div>
            </template>
          </el-upload>
          <div v-if="contractForm.file" class="current-files">
            <div class="file-title">已上传文件：</div>
            <div class="file-items">
              <el-tag
                v-for="(file, index) in getFileList(contractForm.file)"
                :key="index"
                closable
                @close="handleRemoveFile(file, index)"
                class="file-tag">
                {{ getFileName(file) }}
              </el-tag>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="contractForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入合同备注信息" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'ContractPage',
  data() {
    return {
      loading: false,
      contractList: [],
      projectList: [],
      selectedContracts: [],
      searchForm: {
        name: '',
        project: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新建合同',
      submitting: false,
      contractForm: {
        id: '',
        name: '',
        project: [],
        file: '',
        remark: ''
      },
      contractRules: {
        name: [
          { required: true, message: '请输入合同名称', trigger: 'blur' },
          { min: 2, max: 255, message: '合同名称长度在 2 到 255 个字符', trigger: 'blur' }
        ]
      },
      // 文件上传相关
      uploadUrl: 'https://erp.dynamic365.cn/api/common/upload', // 文件上传接口
      headers: {
        'token': localStorage.getItem('token')
      },
      uploadData: {
        type: 'contract'
      },
      fileList: []
    }
  },
  computed: {
    contractFormRef() {
      return this.$refs.contractFormRef
    }
  },
  mounted() {
    // 先加载项目列表，再加载合同列表
    this.getProjectList().then(() => {
      this.getContractList()
    })
  },
  methods: {
    // 获取项目列表
    async getProjectList() {
      try {
        const response = await this.$axios.get('/api/project/getlist')
        if (response.code === 1) {
          this.projectList = response.data || []
        } else {
          ElMessage.error(response.msg || '获取项目列表失败')
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        ElMessage.error('获取项目列表失败')
      }
    },

    // 获取合同列表
    async getContractList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        }
        
        const response = await this.$axios.get('/api/contract/getlist', { params })
        if (response.code === 1) {
          this.contractList = response.data || []
          this.pagination.total = response.total || 0
        } else {
          ElMessage.error(response.msg || '获取合同列表失败')
        }
      } catch (error) {
        console.error('获取合同列表失败:', error)
        ElMessage.error('获取合同列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.getContractList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        name: '',
        project: ''
      }
      this.pagination.currentPage = 1
      this.getContractList()
    },

    // 新建合同
    handleAdd() {
      this.dialogTitle = '新建合同'
      this.contractForm = {
        id: '',
        name: '',
        project: [],
        file: ''
      }
      this.fileList = []
      this.dialogVisible = true
    },

    // 编辑合同
    handleEdit(row) {
      this.dialogTitle = '编辑合同'
      this.contractForm = {
        ...row,
        project: this.getProjectIds(row.project) // 将字符串转换为数组
      }
      this.fileList = this.getFileList(row.file).map(file => ({
        name: this.getFileName(file),
        url: file
      }))
      this.dialogVisible = true
    },

    // 删除合同
    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除合同 "${row.name}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await this.$axios.post('/contract/delete', { id: row.id })
        if (response.code === 1) {
          ElMessage.success('删除成功')
          this.getContractList()
        } else {
          ElMessage.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除合同失败:', error)
          ElMessage.error('删除失败')
        }
      }
    },

    // 刷新数据
    handleRefresh() {
      this.getContractList()
      ElMessage.success('数据已刷新')
    },

    // 提交表单
    async handleSubmit() {
      if (!this.contractFormRef) return

      try {
        await this.contractFormRef.validate()
        this.submitting = true

        // 将项目数组转换为逗号分隔的字符串
        const submitData = {
          ...this.contractForm,
          project: this.contractForm.project.join(',')
        }

        let response
        if (this.contractForm.id) {
          // 编辑合同
          response = await this.$axios.post('/contract/edit', submitData)
        } else {
          // 新建合同
          response = await this.$axios.post('/contract/add', submitData)
        }

        if (response.code === 1) {
          ElMessage.success(this.contractForm.id ? '编辑成功' : '新建成功')
          this.dialogVisible = false
          this.getContractList()
        } else {
          ElMessage.error(response.msg || '操作失败')
        }
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.msg || '操作失败')
        }
      } finally {
        this.submitting = false
      }
    },

    // 对话框关闭
    handleDialogClose() {
      this.contractFormRef?.resetFields()
      this.fileList = []
    },

    // 选择合同
    handleSelectionChange(selection) {
      this.selectedContracts = selection
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.getContractList()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.getContractList()
    },

    // 获取项目名称
    getProjectName(projectId) {
      const proj = this.projectList.find(item => item.id == projectId)
      return proj ? proj.name : '未知项目'
    },

    // 将项目字符串转换为数组
    getProjectIds(projectString) {
      if (!projectString) return []
      return projectString.split(',').filter(id => id.trim())
    },

    // 将文件字符串转换为数组
    getFileList(fileString) {
      if (!fileString) return []
      return fileString.split(',').filter(file => file.trim())
    },

    // 获取文件名
    getFileName(filePath) {
      if (!filePath) return ''
      const parts = filePath.split('/')
      return parts[parts.length - 1]
    },

    // 文件预览
    handleFilePreview(file) {
      window.open(file, '_blank')
    },

    // 文件上传前验证
    beforeUpload(file) {
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        ElMessage.error('文件大小不能超过 10MB!')
        return false
      }
      return true
    },

    // 文件上传成功
    handleUploadSuccess(response, file) {
      if (response.code === 1) {
        // 将新文件添加到文件列表中
        const newFile = response.data.url || response.data.path
        const currentFiles = this.contractForm.file ? this.contractForm.file.split(',') : []
        currentFiles.push(newFile)
        this.contractForm.file = currentFiles.join(',')
        
        ElMessage.success('文件上传成功')
      } else {
        ElMessage.error(response.msg || '文件上传失败')
      }
    },

    // 文件上传失败
    handleUploadError(error, file) {
      console.error('文件上传失败:', error)
      ElMessage.error('文件上传失败')
    },

    // 移除文件
    handleFileRemove(file, fileList) {
      // 从文件列表中移除
      const currentFiles = this.contractForm.file ? this.contractForm.file.split(',') : []
      const fileIndex = currentFiles.findIndex(f => f === file.url || f === file.response?.data?.url)
      if (fileIndex > -1) {
        currentFiles.splice(fileIndex, 1)
        this.contractForm.file = currentFiles.join(',')
      }
    },

    // 移除已上传文件
    handleRemoveFile(file, index) {
      const currentFiles = this.contractForm.file ? this.contractForm.file.split(',') : []
      currentFiles.splice(index, 1)
      this.contractForm.file = currentFiles.join(',')
    },

    // 格式化日期
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.contract-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.page-header p {
  margin: 5px 0 0 0;
  color: #909399;
  font-size: 14px;
}

.content-area {
  margin-top: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-area {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.pagination-area {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.project-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.project-tag {
  margin: 2px;
}

.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.file-tag {
  margin: 2px;
  cursor: pointer;
}

.file-tag:hover {
  opacity: 0.8;
}

.current-files {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.file-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.file-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.upload-demo {
  width: 100%;
}
</style>
